<template>
  <div class="phone-input">
    <div class="labeled-input">
      <label v-if="label" class="input-label" :for="inputId">{{ label }}</label>
      <input
        :id="inputId"
        type="tel"
        class="input-text"
        :class="{ 'input-text--has-error': !!errorMessage }"
        :placeholder="placeholder"
        :value="phoneDisplay"
        @input="handleInput"
        @keydown="handleKeydown"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </div>
    <span v-if="errorMessage" class="phone-input__error">{{ errorMessage }}</span>
  </div>
</template>

<script>
import { RandomId } from '@/shared/lib/random';

const { generateRandomId } = RandomId();

const PHONE_PREFIX = '+38 (';

export default {
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Телефон',
    },
    placeholder: {
      type: String,
      default: '+38 (0__) ___-__-__',
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      inputId: generateRandomId(),
      phoneDisplay: this.value ? this.formatPhone(this.value) : '',
    };
  },
  watch: {
    value(value) {
      if (!value) {
        if (this.phoneDisplay !== PHONE_PREFIX) {
          this.phoneDisplay = '';
        }
        return;
      }

      if (value !== this.toDigits(this.phoneDisplay)) {
        this.phoneDisplay = this.formatPhone(value);
      }
    },
  },
  methods: {
    toDigits(value) {
      const digits = (value || '').replace(/\D/g, '');
      const ukraineDigits = digits.startsWith('38') ? digits.slice(2) : digits;

      return ukraineDigits ? '38' + ukraineDigits : '';
    },
    formatPhone(value) {
      const digits = value.replace(/\D/g, '');
      const ukraineDigits = digits.startsWith('38') ? digits.slice(2) : digits;

      let formatted = PHONE_PREFIX;

      if (ukraineDigits.length > 0) {
        formatted += ukraineDigits.substring(0, 3);
      }

      if (ukraineDigits.length >= 3) {
        formatted += ') ' + ukraineDigits.substring(3, 6);
      }

      if (ukraineDigits.length >= 6) {
        formatted += '-' + ukraineDigits.substring(6, 8);
      }

      if (ukraineDigits.length >= 8) {
        formatted += '-' + ukraineDigits.substring(8, 10);
      }

      return formatted;
    },
    update(display, input) {
      this.phoneDisplay = display;
      input.value = display;
      this.$emit('input', this.toDigits(display));

      this.$nextTick(() => {
        input.setSelectionRange(display.length, display.length);
      });
    },
    handleInput(event) {
      const input = event.target;
      const digits = input.value.replace(/\D/g, '');

      if (digits.length <= 12) {
        this.update(this.formatPhone(input.value), input);
      } else {
        input.value = this.phoneDisplay;
      }
    },
    handleKeydown(event) {
      if (event.key !== 'Backspace' && event.key !== 'Delete') {
        return;
      }

      event.preventDefault();

      const input = event.target;
      const minCursorPosition = event.key === 'Backspace' ? PHONE_PREFIX.length + 1 : PHONE_PREFIX.length;
      if (input.selectionStart < minCursorPosition) {
        return;
      }

      const digits = input.value.replace(/\D/g, '');
      const ukraineDigits = digits.startsWith('38') ? digits.slice(2) : digits;

      if (ukraineDigits.length > 0) {
        this.update(this.formatPhone('38' + ukraineDigits.slice(0, -1)), input);
      }
    },
    handleFocus(event) {
      if (!this.phoneDisplay) {
        this.phoneDisplay = PHONE_PREFIX;
      }

      this.$nextTick(() => {
        if (event.target.value === PHONE_PREFIX) {
          event.target.setSelectionRange(PHONE_PREFIX.length, PHONE_PREFIX.length);
        }
      });
    },
    handleBlur() {
      if (this.phoneDisplay === PHONE_PREFIX) {
        this.phoneDisplay = '';
        this.$emit('input', '');
      }

      this.$emit('blur');
    },
  },
};
</script>

<style lang="scss" scoped>
.labeled-input {
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}

.phone-input__error {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #FF3B30;
  margin-top: -12px;
  margin-bottom: 8px;
  padding-left: 4px;
}
</style>
